import { uuidv7 as uuid } from "uuidv7";

import { capitalizeFirstLetter, isStringUuid } from "src/utils/methods";
import { generateRankString } from "src/utils/rankStrings";
import { tableDataType } from "src/app/components/Table/Table";
import { Dispatch } from "redux";
import {
  createTaskMetaField,
  createGlobalTaskMetaField,
  createNewTaskGlobalField,
  updateGlobalTaskField,
  updateTaskMetaField,
} from "src/redux";
import { Identity, TaskType } from "src/types";
import { TasksColumn } from "src/redux/task/taskReducer";
import {
  DynamicValueProviderName,
  MetaFieldType,
} from "src/app/methods/getMetaFieldTypeOptions";
import { DEFAULT_RANK } from "src/constants/ranks";
import {
  IAccessLinkData,
  MFState,
  SetStateFn,
  MetaFieldTypeOption,
  SelectedFormulaElement,
  AiFieldSettings,
} from "../types";
import {
  METRIC_TYPES,
  mathOperatorOptions,
  resultFormatOptions,
} from "../utils";

interface Props {
  data: any;
  metaFieldTypeOptions: MetaFieldTypeOption[];
  salesMetricOptions: any;
  setBasicTypes: any;
  setState: SetStateFn;
  formulaElementOptions: SelectedFormulaElement[];
}

export const setTaskFieldData = async (functionProps: Props) => {
  const {
    data,
    metaFieldTypeOptions,
    salesMetricOptions,
    setBasicTypes,
    setState,
    formulaElementOptions,
  } = functionProps;

  const source = data.metaFieldSource;
  const initialData = data.metaFieldOptions;

  const sourceType = source?.split(".")[0];
  const isSalesMetrics = sourceType === "wsCouponRedemptions";
  const isSocialMetrics =
    sourceType === "socialProfile" || sourceType === "publication";
  const isRelatedField = sourceType === "relatedMetaField";

  let fieldType = data.type;
  if (initialData.arithmeticFieldOptions) {
    fieldType = MetaFieldType.Formula;
  } else if (isSalesMetrics) {
    fieldType = MetaFieldType.SalesMetrics;
  } else if (isSocialMetrics) {
    fieldType = MetaFieldType.Metrics;
  } else if (isRelatedField) {
    fieldType = MetaFieldType.RelatedField;
  } else if (source === MetaFieldType.EMV) {
    fieldType = MetaFieldType.EMV;
  }

  if (data.taskType === TaskType.Creator && data.description) {
    setState("showInCreatorDatabase", data.showInCreatorDatabase);
  }

  if (data.type === MetaFieldType.DictionaryElement && !isRelatedField) {
    if (
      initialData.wsDictionaryElementsFilter !== null &&
      typeof initialData.wsDictionaryElementsFilter === "object"
    ) {
      setState("filterElementsFields", initialData.wsDictionaryElementsFilter);
      setState("areFilterElementsEnabled", true);
    }

    setState(
      "selectedOption",
      metaFieldTypeOptions.find((option) =>
        option.value.includes(initialData.wsDictionaryUuid),
      ) || {},
    );
  } else {
    setState(
      "selectedOption",
      metaFieldTypeOptions.find((option) => option.value === fieldType) || {},
    );
  }

  switch (fieldType) {
    case MetaFieldType.Text:
    case MetaFieldType.Number:
    case MetaFieldType.Currency:
    case MetaFieldType.Date:
    case MetaFieldType.BoolVal:
    case MetaFieldType.Rating:
    case MetaFieldType.Percent:
      {
        const aiPromptValue = initialData?.aiMetaValueService?.message;
        if (typeof aiPromptValue === "string") {
          setState("aiPrompt", aiPromptValue);
          setState("areAiFieldsEnabled", true);

          // Load selected AI metafield UUIDs
          const taskMetaFieldUuids =
            initialData?.aiMetaValueService?.inputs?.wsTaskMetaFieldUuids || [];
          const projectMetaFieldUuids =
            initialData?.aiMetaValueService?.inputs?.wsProjectMetaFieldUuids ||
            [];

          // Store the UUIDs temporarily - they will be mapped to full objects with labels in AiFieldsAssistant
          const selectedUuids = [
            ...taskMetaFieldUuids.map((uuid: string) => ({
              value: uuid,
              isTaskMetaField: true,
            })),
            ...projectMetaFieldUuids.map((uuid: string) => ({
              value: uuid,
              isTaskMetaField: false,
            })),
          ];

          if (selectedUuids.length > 0) {
            setState("selectedAiMetafields", selectedUuids);
          }
        }
      }
      setBasicTypes(fieldType, initialData);
      break;
    case MetaFieldType.Formula:
      {
        const formulaArithmeticsDataProps = {
          formulaArithmetics: initialData.arithmeticFieldOptions,
          formulaElementOptions,
          setState,
        };

        setFormulaArithmeticsData(formulaArithmeticsDataProps);
      }
      break;

    case MetaFieldType.SalesMetrics:
      {
        const selectedSalesMetric = salesMetricOptions?.find(
          (item: any) => item.value === source,
        );

        setState(
          "selectedMetrics",
          selectedSalesMetric ? [selectedSalesMetric] : [],
        );
      }
      break;
    case MetaFieldType.Member:
      setState("filterMemberByTeam", initialData.wsTeamUuids?.length > 0);
      setState("wsTeamUuids", initialData.wsTeamUuids ?? []);
      break;
    case MetaFieldType.DictionaryElement:
      setState(
        "enableDictionarySubField",
        initialData?.wsDictionarySubFieldUuid,
      );
      setState(
        "wsDictionarySubFieldUuid",
        initialData?.wsDictionarySubFieldUuid ?? null,
      );

      break;
    case MetaFieldType.EMV:
      setState("emvValues", initialData.emvValues ?? {});
      setBasicTypes(fieldType, initialData);
      break;

    default:
      setBasicTypes(fieldType, initialData);
  }
};

interface FormulaArithmeticsDataProps {
  formulaArithmetics: any;
  formulaElementOptions: SelectedFormulaElement[];
  setState: SetStateFn;
}

const setFormulaArithmeticsData = (props: FormulaArithmeticsDataProps) => {
  const { formulaArithmetics, formulaElementOptions, setState } = props;

  const operator = mathOperatorOptions.find(
    (item) => item.value === formulaArithmetics.operator,
  );

  const resultFormat = resultFormatOptions.find(
    (item) => item.value === formulaArithmetics.resultFormat,
  );

  let elA;
  let elB;

  if (isStringUuid(formulaArithmetics.elements[0])) {
    elA = formulaElementOptions.find(
      (item) => item?.value?.id === formulaArithmetics.elements[0],
    );
  } else {
    elA = {
      value: formulaArithmetics.elements[0],
      label: formulaArithmetics.elements[0],
    };
  }

  if (isStringUuid(formulaArithmetics.elements[1])) {
    elB = formulaElementOptions.find(
      (item) => item?.value?.id === formulaArithmetics.elements[1],
    );
  } else {
    elB = {
      value: formulaArithmetics.elements[1],
      label: formulaArithmetics.elements[1],
    };
  }

  setState("selectedFormulaOperator", operator);
  setState("selectedResultFormat", resultFormat);
  setState("selectedFormulaElements", { a: elA ?? {}, b: elB ?? {} });
};

export const getTaskMetaFieldType = (
  metaFieldType: string | undefined,
  selectedResultFormat: any,
  workspaceDictionaryList: Identity["workspaceDictionaryList"],
) => {
  const type = metaFieldType?.split("_")[0];

  if (
    workspaceDictionaryList?.length > 0 &&
    workspaceDictionaryList.find(
      (item) =>
        item?.wsDictionaryElementName?.toLowerCase() === type?.toLowerCase(),
    )
  ) {
    return "dictionaryElement";
  }

  switch (type) {
    case "relatedField":
      return null;

    case "formula":
      return selectedResultFormat.value;

    case "brief":
    case "client":
    case "brand":
    case "agency":
      return "dictionaryElement";
    case MetaFieldType.EMV:
      return MetaFieldType.Currency;
    default:
      return metaFieldType || "text";
  }
};

interface CreateProps {
  taskType: TaskType;
  dataType: tableDataType;
  dispatch: Dispatch<any>;
  state: MFState;
  projectId: string;
  fieldName: string;
  tasksColumns: TasksColumn[];
  metaFieldType: any;
  accessLinkData: IAccessLinkData;
  activeWorkspaceUuid: string;
  workspaceDictionaryList: Identity["workspaceDictionaryList"];
  hasAccessToAiFields: boolean;
  globalFieldMode?: boolean;
  onSuccessCallback?: (uuids?: string[]) => void;
}

export const createTaskField = async (
  functionProps: CreateProps,
  context?: tableDataType,
) => {
  const {
    tasksColumns,
    state: {
      description,
      selectedFormulaElements,
      selectedResultFormat,
      selectedFormulaOperator,
      selectedOption,
      selectedMetrics,
      selectedCurrency,
      globalMetaFieldData,
      wsTeamUuids,
      showInCreatorDatabase,
      enableDictionarySubField,
      wsDictionarySubFieldUuid,
      relatedMetaField,
      relatedMetaFieldAggregationType,
      relatedDictionaryMetaFieldUuid,
      filterMemberByTeam,
      emvValues,
      filterElementsFields,
      nameEdited,
      selectedPlatform,
      aiPrompt,
      selectedAiMetafields,
      areAiFieldsEnabled,
      selectedAudienceCountry,
    },
    dispatch,
    projectId,
    fieldName,
    metaFieldType,
    taskType,
    dataType,
    accessLinkData,
    activeWorkspaceUuid,
    workspaceDictionaryList,
    globalFieldMode,
    onSuccessCallback,
    hasAccessToAiFields,
  } = functionProps;

  const createdFieldsIds: string[] = [];

  const arithmeticFieldOptions = {
    elements: [
      selectedFormulaElements.a.value?.id || selectedFormulaElements.a.value,
      selectedFormulaElements.b.value?.id || selectedFormulaElements.b.value,
    ],
    resultFormat: selectedResultFormat.value,
    operator: selectedFormulaOperator.value,
  };
  const formulaCurrency =
    selectedFormulaElements.a.value?.currencyCode ||
    selectedFormulaElements.b.value?.currencyCode;
  const fieldType = getTaskMetaFieldType(
    metaFieldType,
    selectedResultFormat,
    workspaceDictionaryList,
  );

  let valueSource: null | string = null;
  if (METRIC_TYPES.includes(selectedOption.value)) {
    valueSource = selectedMetrics[0]?.value;
  } else if (selectedOption.value === MetaFieldType.AccessLink) {
    valueSource = MetaFieldType.AccessLink;
  } else if (selectedOption.value === MetaFieldType.EMV) {
    valueSource = MetaFieldType.EMV;
  } else if (selectedOption.value === MetaFieldType.Project) {
    valueSource = DynamicValueProviderName.Project;
  }

  let currencyCode: null | string = null;
  if (
    formulaCurrency ||
    selectedOption.value === MetaFieldType.Currency ||
    selectedOption.value === MetaFieldType.EMV
  ) {
    currencyCode = formulaCurrency || (selectedCurrency.value ?? null);
  }

  let aiFieldSettings: AiFieldSettings | undefined;
  if (hasAccessToAiFields) {
    let aiTaskMetaFieldUuids: string[] = [];
    let aiProjectMetaFieldUuids: string[] = [];
    if (areAiFieldsEnabled) {
      aiTaskMetaFieldUuids = selectedAiMetafields
        .filter((field) => field.isTaskMetaField)
        .map((field) => field.value);
      aiProjectMetaFieldUuids = selectedAiMetafields
        .filter((field) => !field.isTaskMetaField)
        .map((field) => field.value);
    }

    aiFieldSettings = {
      wsAiMetaValueServiceMessage: aiPrompt.length > 0 ? aiPrompt : null,
      wsAiMetaValueServiceTaskMetaFieldUuids:
        aiTaskMetaFieldUuids.length > 0 ? aiTaskMetaFieldUuids : null,
      wsAiMetaValueServiceProjectMetaFieldUuids:
        aiProjectMetaFieldUuids.length > 0 ? aiProjectMetaFieldUuids : null,
    };
  }

  const id = uuid();

  if (globalFieldMode) {
    createdFieldsIds.push(id);
    dispatch(
      createNewTaskGlobalField({
        wsWorkspaceUuid: activeWorkspaceUuid,
        metaFieldName: fieldName,
        metaFieldUuid: id,
        metaFieldDescription: description,
        metaFieldType: fieldType,
        metaFieldTaskType: taskType,
        metaFieldValueSource: valueSource ?? null,
        metaFieldCurrency: currencyCode,
        arithmeticFieldOptions:
          selectedOption.value === MetaFieldType.Formula
            ? arithmeticFieldOptions
            : null,
        wsDictionaryUuid:
          fieldType === MetaFieldType.DictionaryElement
            ? metaFieldType.split("_")[1]
            : null,
        wsTeamUuids:
          selectedOption.value === MetaFieldType.Member && filterMemberByTeam
            ? wsTeamUuids
            : null,
        showInCreatorDatabase,
        wsDictionarySubFieldUuid:
          fieldType === MetaFieldType.DictionaryElement &&
          enableDictionarySubField
            ? wsDictionarySubFieldUuid
            : null,
        relatedMetaFieldUuid: relatedMetaField?.value?.id ?? null,
        relatedMetaFieldAggregationType:
          relatedMetaFieldAggregationType?.value ?? null,
        relatedDictionaryMetaFieldUuid:
          relatedDictionaryMetaFieldUuid.value ?? null,
        emvValues:
          selectedOption.value === MetaFieldType.EMV ? emvValues : null,
        wsDictionaryElementsFilter: filterElementsFields ?? null,
        ...(aiFieldSettings ?? {}),
      }),
    );
  } else if (globalMetaFieldData.length > 0) {
    let prevRank = "";
    const globalMetaFieldDataForBackend = globalMetaFieldData.map(
      (field, i) => {
        let rank;

        if (i === 0) {
          const lastElementRank =
            tasksColumns[tasksColumns.length - 1]?.metaFieldRank ||
            DEFAULT_RANK;
          rank = generateRankString(lastElementRank, "");
          prevRank = rank;
        } else {
          rank = generateRankString(prevRank, "");
          prevRank = rank;
        }

        const id = uuid();
        createdFieldsIds.push(id);
        return {
          wsGlobalTaskMetaFieldUuid: field.uuid,
          wsProjectUuid: projectId,
          wsTaskMetaFieldRank: rank,
          wsTaskMetaFieldUuid: id,
          taskType:
            dataType === tableDataType.RecruitmentForm
              ? TaskType.Creator
              : taskType,
          wsTeamUuids:
            selectedOption.value === MetaFieldType.Member && filterMemberByTeam
              ? wsTeamUuids
              : null,
        };
      },
    );

    await dispatch(createGlobalTaskMetaField(globalMetaFieldDataForBackend));
  } else {
    const getMetafields = () => {
      let previousRank = generateRankString(
        tasksColumns[tasksColumns.length - 1]?.metaFieldRank || DEFAULT_RANK,
        "",
      );

      const commonPart = {
        taskType:
          context === tableDataType.RecruitmentForm
            ? TaskType.Creator
            : taskType,
        currencyCode,
        iconName: selectedMetrics[0]?.icon?.name,
        iconColor: selectedMetrics[0]?.icon?.color,
        wsMetaFieldsToCreateAccess:
          selectedOption.value === MetaFieldType.AccessLink
            ? accessLinkData
            : null,
        arithmeticFieldOptions:
          selectedOption.value === MetaFieldType.Formula
            ? arithmeticFieldOptions
            : null,
        wsDictionaryUuid:
          fieldType === MetaFieldType.DictionaryElement
            ? metaFieldType.split("_")[1]
            : null,
        wsTeamUuids:
          selectedOption.value === MetaFieldType.Member && filterMemberByTeam
            ? wsTeamUuids
            : null,
        wsDictionarySubFieldUuid:
          fieldType === MetaFieldType.DictionaryElement &&
          enableDictionarySubField
            ? wsDictionarySubFieldUuid
            : null,
        relatedMetaFieldUuid: relatedMetaField?.value?.id,
        relatedMetaFieldAggregationType: relatedMetaFieldAggregationType?.value,
        relatedDictionaryMetaFieldUuid: relatedDictionaryMetaFieldUuid?.value,
        emvValues:
          selectedOption.value === MetaFieldType.EMV ? emvValues : null,
        wsDictionaryElementsFilter: filterElementsFields ?? null,
      };

      // For now metric type is the only one which allows creating multiple fields at once
      if (selectedMetrics.length > 0) {
        return selectedMetrics.map((metric) => {
          previousRank = generateRankString(previousRank, "");

          let metricValue = metric.value;
          let suffix = "";

          const isCountryMetric = metric.value.includes(
            "audienceGeographicalReach.",
          );

          if (isCountryMetric && selectedAudienceCountry.value) {
            metricValue = metricValue.replace(
              ":country:",
              selectedAudienceCountry?.value,
            );
            suffix = ` ${selectedAudienceCountry?.value}`;
          }

          const isMulti = selectedMetrics.length > 1;
          const prefix = selectedPlatform.value
            ? `${capitalizeFirstLetter(selectedPlatform.value)} `
            : "";
          const dynamicName = `${prefix}${metric.label}${suffix}`;
          const singleName = nameEdited ? fieldName : dynamicName;
          const name = isMulti ? dynamicName : singleName;

          return {
            metaFieldName: name,
            metaFieldType: metric.type,
            metaFieldId: uuid(),
            metaFieldRank: previousRank,
            metaFieldValueSource: metricValue,
            ...commonPart,
          };
        });
      }

      return [
        {
          metaFieldName: fieldName,
          metaFieldType: fieldType,
          metaFieldId: id,
          metaFieldRank: previousRank,
          ...(valueSource && {
            metaFieldValueSource: valueSource,
          }),
          ...commonPart,
        },
      ];
    };

    const wsTaskMetaFields = getMetafields();
    createdFieldsIds.push(...wsTaskMetaFields.map((item) => item.metaFieldId));

    await dispatch(
      createTaskMetaField({
        wsProjectUuid: projectId,
        wsTaskMetaFields,
      }),
    );
  }
  if (dataType === tableDataType.RecruitmentForm && onSuccessCallback) {
    onSuccessCallback(createdFieldsIds);
  }
};

interface UpdateProps {
  data: any;
  dispatch: Dispatch<any>;
  state: MFState;
  accessLinkData: IAccessLinkData;
  activeWorkspaceUuid: string;
  editingGlobalField?: boolean;
  workspaceDictionaryList: Identity["workspaceDictionaryList"];
  hasAccessToAiFields: boolean;
}

export const updateTaskField = async (functionProps: UpdateProps) => {
  const {
    data,
    dispatch,
    state: {
      description,
      fieldName,
      selectedOption,
      selectedCurrency,
      selectedFormulaElements,
      selectedFormulaOperator,
      selectedMetrics,
      selectedResultFormat,
      wsTeamUuids,
      showInCreatorDatabase,
      enableDictionarySubField,
      wsDictionarySubFieldUuid,
      relatedMetaField,
      relatedMetaFieldAggregationType,
      relatedDictionaryMetaFieldUuid,
      filterMemberByTeam,
      emvValues,
      aiPrompt,
      selectedAiMetafields,
      areAiFieldsEnabled,
    },
    accessLinkData,
    activeWorkspaceUuid,
    editingGlobalField,
    workspaceDictionaryList,
    hasAccessToAiFields,
  } = functionProps;

  const fieldType = getTaskMetaFieldType(
    selectedOption.value,
    selectedResultFormat,
    workspaceDictionaryList,
  );

  const arithmeticFieldOptions = {
    elements: [
      selectedFormulaElements.a.value?.id || selectedFormulaElements.a.value,
      selectedFormulaElements.b.value?.id || selectedFormulaElements.b.value,
    ],
    resultFormat: selectedResultFormat.value,
    operator: selectedFormulaOperator.value,
  };
  const formulaCurrency =
    selectedFormulaElements.a.value?.currencyCode ||
    selectedFormulaElements.b.value?.currencyCode;

  let currencyCode = null;
  if (
    formulaCurrency ||
    selectedOption.value === MetaFieldType.Currency ||
    selectedOption.value === MetaFieldType.EMV
  ) {
    currencyCode = selectedCurrency?.value;
  }

  let aiFieldSettings: AiFieldSettings | undefined;
  if (hasAccessToAiFields) {
    let aiTaskMetaFieldUuids: string[] = [];
    let aiProjectMetaFieldUuids: string[] = [];
    if (areAiFieldsEnabled) {
      aiTaskMetaFieldUuids = selectedAiMetafields
        .filter((field) => field.isTaskMetaField)
        .map((field) => field.value);
      aiProjectMetaFieldUuids = selectedAiMetafields
        .filter((field) => !field.isTaskMetaField)
        .map((field) => field.value);
    }

    aiFieldSettings = {
      wsAiMetaValueServiceMessage: aiPrompt.length > 0 ? aiPrompt : null,
      wsAiMetaValueServiceTaskMetaFieldUuids:
        aiTaskMetaFieldUuids.length > 0 ? aiTaskMetaFieldUuids : null,
      wsAiMetaValueServiceProjectMetaFieldUuids:
        aiProjectMetaFieldUuids.length > 0 ? aiProjectMetaFieldUuids : null,
    };
  }

  if (editingGlobalField) {
    dispatch(
      updateGlobalTaskField({
        wsWorkspaceUuid: activeWorkspaceUuid,
        metaFieldName: fieldName,
        metaFieldUuid: data.id,
        metaFieldDescription: description,
        metaFieldCurrency: currencyCode,
        arithmeticFieldOptions:
          selectedOption.value === "formula" ? arithmeticFieldOptions : null,
        showInCreatorDatabase,
        metaFieldValueSource:
          selectedOption.value === "accessLink"
            ? "accessLink"
            : selectedMetrics[0]?.value,
        wsDictionarySubFieldUuid:
          fieldType === "dictionaryElement" && enableDictionarySubField
            ? wsDictionarySubFieldUuid
            : null,
        wsTeamUuids:
          selectedOption.value === "member" && filterMemberByTeam
            ? wsTeamUuids
            : null,
        wsDictionaryUuid:
          fieldType === "dictionaryElement"
            ? selectedOption.value.split("_")[1]
            : null,
        emvValues:
          selectedOption.value === MetaFieldType.EMV ? emvValues : null,
        ...(aiFieldSettings ?? {}),
      }),
    );
  } else {
    dispatch(
      updateTaskMetaField({
        metaFieldId: data.id,
        metaFieldNameUpdated: fieldName,
        currencyCode:
          formulaCurrency ||
          (selectedOption.value === "currency"
            ? selectedCurrency?.value
            : null),
        metaFieldValueSource:
          selectedOption.value === "accessLink"
            ? "accessLink"
            : selectedMetrics[0]?.value,
        wsMetaFieldsToCreateAccess:
          selectedOption.value === "accessLink" && accessLinkData,
        arithmeticFieldOptions:
          selectedOption.value === "formula" ? arithmeticFieldOptions : null,
        wsTeamUuids:
          selectedOption.value === "member" && filterMemberByTeam
            ? wsTeamUuids
            : null,
        wsDictionarySubFieldUuid:
          fieldType === "dictionaryElement" && enableDictionarySubField
            ? wsDictionarySubFieldUuid
            : null,
        relatedMetaFieldUuid: relatedMetaField.value?.id,
        relatedMetaFieldAggregationType: relatedMetaFieldAggregationType?.value,
        relatedDictionaryMetaFieldUuid: relatedDictionaryMetaFieldUuid?.value,
        emvValues:
          selectedOption.value === MetaFieldType.EMV ? emvValues : null,
        ...(aiFieldSettings ?? {}),
      }),
    );
  }
};
